import { inject, Injectable } from '@angular/core';
import { environment } from '@work-frontend-monorepo/work-environments';
import { AlertDialogService } from '../alert-dialog/alert-dialog.service';

/**
 * Service zum Überprüfen ob eine neue Version der App deployed wurde.
 * Vergleicht einen Hash in einer version.json Datei.
 *
 * Verwendung:
 * 1. Erstelle eine version.json im dist Ordner mit { "version": "BUILD_HASH" }
 * 2. Rufe checkForNewVersion() periodisch auf
 * 3. Bei neuer Version kann User benachrichtigt werden
 */
@Injectable({
  providedIn: 'root',
})
export class AppVersionCheckService {
  private alertDialogService = inject(AlertDialogService);
  private currentVersion: string | null = null;
  private checkInterval: number | null = null;
  private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 Minuten
  private appName = 'REPLACE_WITH_APP_NAME';

  /**
   * Startet die periodische Überprüfung auf neue Versionen
   */
  public startVersionCheck(appName: string): void {
    this.appName = appName;
    this.fetchCurrentVersion().then(() => {
      this.checkInterval = window.setInterval(() => {
        this.checkForNewVersion().then((hasNewVersion) => {
          if (hasNewVersion) {
            this.showAlert();
          }
        });
      }, this.CHECK_INTERVAL_MS);
    });
  }

  /**
   * Hardcoded Text without "translate" is fine here, just in case there is a chunk loading error with translate service.
   */
  public showAlert() {
    this.alertDialogService.showAlert({
      title: 'Update',
      message: `Es steht eine aktualisierte Version vom ${environment.tenantCompanyCommonName} ${this.appName} bereit. Bitte lade bei nächster Gelegenheit die Seite neu, um mögliche Probleme zu vermeiden.`,
      feedbackState: 'warning',
      actionLabel: 'Seite neu laden',
      alertLifeTimeInMs: 1000 * 60, // 1 minute
      actionHandler: () => {
        window.location.reload();
      },
    });
  }

  /**
   * Prüft ob eine neue Version verfügbar ist
   */
  public async checkForNewVersion(): Promise<boolean> {
    try {
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const serverVersion = data.version;

      if (!this.currentVersion) {
        this.currentVersion = serverVersion;
        return false;
      }

      if (this.currentVersion !== serverVersion) {
        console.info('[VersionCheck] New version detected:', {
          current: this.currentVersion,
          new: serverVersion,
        });
        return true;
      } else {
        console.info('[VersionCheck] No new version detected, all fine.');
      }

      return false;
    } catch (error) {
      console.warn('[VersionCheck] Failed to check version:', error);
      return false;
    }
  }

  /**
   * Lädt die aktuelle Version
   */
  private async fetchCurrentVersion(): Promise<void> {
    try {
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-cache',
      });

      if (response.ok) {
        const data = await response.json();
        this.currentVersion = data.version;
        console.info('[VersionCheck] Current version:', this.currentVersion);
      }
    } catch (error) {
      console.warn('[VersionCheck] Failed to fetch current version:', error);
    }
  }
}
